import React, { useState, useEffect } from 'react';
import { Image, Layout, Menu, Carousel, Collapse } from 'components/shared/AntComponent';
import { Button } from 'components/shared/ButtonComponent';
import EEGLogo from 'assets/img/brandname.png';
import LoginImg from 'assets/img/login-image.png';
import {
    BrainIcon, RightArrowIcon, HippaIcon, EnergyIcon, MouseIcon,
    QuestionMarkIcon, DownChartIcon, WarningIcon, BarIcon, DocumentIcon, UsersIcon,
    UserAddIcon, UploadsIcon, FileCheckIcon, LabIcon, ExternalLinkIcon
} from 'assets/img/custom-icons';
import { CheckOutlined, MobileOutlined, MailOutlined, PhoneOutlined, MenuOutlined, CloseOutlined } from 'components/shared/AntIcons';
import { currentYear } from 'components/shared/CompVariables';
import Image1 from 'assets/img/sample-report-images/1.jpg';
import Image2 from 'assets/img/sample-report-images/2.jpg';
import Image3 from 'assets/img/sample-report-images/3.jpg';
import Image4 from 'assets/img/sample-report-images/4.jpg';
import AxonWaveImg from 'assets/img/sample-report-images/axonwave-logo.png';
import AxonImg from 'assets/img/sample-report-images/axon-logo.png';
import 'assets/styles/landingpage.scss';
import EEGLogo1 from 'assets/img/logo.png';
import { useNavigate } from 'react-router-dom';

const { Header, Content } = Layout;

const LandingPage: React.FC = () => {
    const navigate = useNavigate();
    const [showMenu, setShowMenu] = useState(false);

    const items = [
        {
            key: 1,
            label: "How it works"
        },
        {
            key: 2,
            label: "Pricing"
        },
        {
            key: 3,
            label: "FAQ"
        },
        {
            key: 4,
            label: "About Us"
        }
    ];

    const problemItems = [
        {
            key: 1, title: "Uncertain Diagnosis",
            content: "Relying on self-reported symptoms and subjective questionnaires leaves room for incomplete or imprecise clinical understanding.",
            icon: <QuestionMarkIcon />
        },
        {
            key: 2, title: "Treatment Guesswork",
            content: "Without objective brain data, selecting interventions can involve trial and error without objective physiological context.",
            icon: <DownChartIcon />
        },
        {
            key: 3, title: "Delayed Progress",
            content: "Patients suffer longer while providers cycle through treatments that may not fully account for individual brain patterns.",
            icon: <WarningIcon />
        }
    ];

    const solutionItems = [
        { key: 1, title: "Objective Brain Mapping", content: "19-channel qEEG analysis reveals patterns invisible to clinical observation alone.", icon: <BrainIcon /> },
        { key: 2, title: "Research-Based Analysis", content: "Compare findings against published research and established reference patterns.", icon: <BarIcon /> },
        { key: 3, title: "Comprehensive Reports", content: "Clear interpretive summaries with visual brain maps and research-referenced clinical considerations.", icon: <DocumentIcon /> },
        {
            key: 4, title: "Expert Interpretation", content: `Every report reviewed by Dr. Steve Rondeau, BCN(EEG), qEEG-DL. On-staff neurlogy available for complex cases.
            Interpretations are provided to support qualified providers and do not replace independent clinical judgment`, icon: <UsersIcon />
        }
    ];

    const plantItems = [
        { key: 1, title: "Create Your Account", content: "Sign up at EEGDataHub.com with your clinic email. Complete the BAA digitally and set up billing in minutes.", icon: <UserAddIcon /> },
        { key: 2, title: "Upload EDF Files", content: "Add your patient and upload both Eyes Open and Eyes Closed EDF recordings. We support Zeto, BrainMaster, Mitsar, CGX, and more.", icon: <UploadsIcon /> },
        { key: 3, title: "Receive Your Report", content: "Access a comprehensive Neuro Performance Report with expert interpretation — typically <72 hours, or less for rush orders.", icon: <FileCheckIcon /> }
    ];

    const priceDetails = [
        { key: 1, content: "19-channel qEEG analysis" },
        { key: 2, content: "Expert interpretation by BCN(EEG), qEEG-DL certified specialist" },
        { key: 3, content: "Comprehensive Neuro Performance Report" },
        { key: 4, content: "Visual brain maps including qEEG metrics, visualizations, and expert interpretation for clinical decision support." },
        { key: 5, content: "HIPAA compliant platform" },
        { key: 6, content: "Rush processing available" }
    ];

    const waveItems = [
        { key: 1, title: "Just Upload Your EDF", content: "No account needed. Simply load your EDF file and explore.", icon: <UploadsIcon /> },
        { key: 2, title: "Instant Visualizations", content: "See your brain data come to life with intuitive displays.", icon: <EnergyIcon /> }
    ];

    const faqItems: any = [
        {
            key: "1", label: "What equipment do I need to get started?",
            children: <p className='normal-font fs-14'>EEG Data Hub works with most major EEG amplifiers including Zeto, BrainMaster,
                Mitsar, CGX, and others. You&apos;ll need the ability to export recordings in EDF format. We recommend minimum 6-10 minute recordings for
                both Eyes Open and Eyes Closed conditions.</p>
        },
        {
            key: "2", label: "How long does it take to receive my report?",
            children: <p className='normal-font fs-14'>Standard turnaround is &lt;72 hours from the time of submission, or less for rush orders.
                Each report is reviewed by Dr. Steven Rondeau, BCN (EEG), QEEG-DL, ensuring expert-level interpretation and quality assurance.</p>
        },
        {
            key: "3", label: "Is EEG Data Hub HIPAA compliant?",
            children: <p className='normal-font fs-14'>Yes, absolutely. We take patient privacy seriously. All data is encrypted, transmitted securely,
                and stored in compliance with HIPAA regulations.
                You&apos;ll sign a Business Associate Agreement (BAA) during account setup.
            </p>
        },
        {
            key: "4", label: "What's included in the Neuro Performance Report?",
            children: <p className='normal-font fs-14'>The comprehensive report includes absolute and relative power analysis, coherence mapping,
                asymmetry patterns, key ratios (Theta/Beta, Alpha/Beta), peak frequency analysis, and expert interpretation with clinical considerations.
                Visual brain maps make findings easy to understand and share with patients. Findings are presented to support provider interpretation and discussion
                and do not constitute diagnoses or treatment directives.
            </p>
        },
        {
            key: "5", label: "Do I need special training to interpret the reports?",
            children: <p className='normal-font fs-14'>No specialized training is required to use our reports. Each report includes plain-language
                summaries and clinical context written by our BCN-certified expert. However, reports are intended as educational resources to support,
                not replace, your clinical judgment.</p>
        },
        {
            key: "6", label: "Can I add staff members to my account?",
            children: <p className='normal-font fs-14'>Yes! As the administrator, you can add team members with different roles and access levels,
                including technicians for recording and billing staff for administration, each with appropriately limited access.</p>
        },
        {
            key: "7", label: "What if I need a clinical EEG review for seizure activity?",
            children: <p className='normal-font fs-14'>Our standard reports focus on qEEG patterns and do not include clinical review for epileptiform activity.
                However, when indicated by symptoms or provider request, reports can be reviewed by a consulting neurologist through EEG Data Hub for additional evaluation.
                Any clinical EEG review is performed by a consulting neurologist and is distinct from EEG Data Hub&apos;s standard qEEG reporting services.
            </p>
        }
    ];

    const ecoItems = [
        { key: 1, title: "EEG Data Hub", content: "Physician-reviewed qEEG reports with fast turnaround", icon: <img src={EEGLogo1} height={40} width="auto" /> },
        { key: 2, title: "AxonWave App", content: "Consumer app for EEG insights on the go", icon: <img src={AxonWaveImg} height={30} width="auto"/> },
        { key: 3, title: "Expert Interpretation", content: "BCN-certified specialists for complex cases", icon: <BrainIcon /> },
        { key: 4, title: "Research Partnerships", content: "Collaborate with our neuroscience team", icon: <LabIcon /> }
    ];

    const navigateSection = (e: any) => {

        let sectionId = '';
        const id = e.key?.toString();
        if (e.key?.toString() == 4) {
            window.open("https://axoneegsolutions.com/about-us/", "_blank");
        } else {
            switch (id) {
                case '1':
                    sectionId = 'how-it-works';
                    break;
                case '2':
                    sectionId = 'pricing';
                    break;
                case '3':
                    sectionId = 'faq';
                    break;

                // Add more cases as needed
                default:
                    return;
            }

            // Method 1: With smooth scroll (recommended)
            const element = document.getElementById(sectionId);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }

            // Optional: Update URL hash
            window.location.hash = sectionId;
        }
    };

    const navigateLogin = () => {
        navigate('/login');
        // const url = window.location.origin;
        // window.open(`${url}/login`, "_blank");
    }

    const navigateAppStore = () => {
        window.open("https://apps.apple.com/us/app/axonwave/id6741162264", "_blank");
    }
    const navigateAxon = () => {
        window.open("https://axoneegsolutions.com/", "_blank");
    }

    const expandMenuOnClick = () => {
        setShowMenu(!showMenu);
    }

    const ecoClick = (e: any) => {
        if (e.key == 3 || e.key == 4) {
            window.open("https://axoneegsolutions.com/", "_blank");
        } else if (e.key == 2) {
            window.open("https://apps.apple.com/us/app/axonwave/id6741162264", "_blank");
        } else {
            return;
        }
    }

    return (
        <div>
            <Layout className='landing-layout'>
                <Header className='my-auto bg-white col-md-12 mx-auto border-bottom'>
                    <div className='d-flex col-md-9 align-items-center justify-content-center m-auto'>
                        <div className="demo-logo col-md-4">
                            <img src={EEGLogo} alt="eeg-logo" height={35} />
                        </div>
                        <div className='ms-auto hidden-menu' onClick={() => expandMenuOnClick()}>
                            {showMenu ? < CloseOutlined />
                                : <MenuOutlined />}
                        </div>

                        <Menu
                            mode="horizontal"
                            className='mx-auto col-md-4 border-0'
                            // defaultSelectedKeys={['2']}
                            onClick={(e) => navigateSection(e)}
                            items={items}
                            style={{ flex: 1, minWidth: 0 }}
                        />
                        <div className='ms-auto col-md-4 landing-header text-end'>
                            <Button type="default" className="bg-white text-dark me-2" onClick={navigateLogin}>Log in</Button>
                            <Button type="primary" className='bg-lightblue' onClick={navigateLogin}>Get Started</Button>
                        </div>
                    </div>
                </Header>
                {showMenu ?
                    <div className="bg-white py-3 px-0 shadow-sm w-100 new-menu">
                        {items?.map((item: any, index: number) => {
                            return (
                                <div className="fs-8 mb-2 px-4" key={index} onClick={() => { navigateSection(item); expandMenuOnClick(); }}>
                                    {item.label}
                                </div>
                            )
                        })}
                        <div className='newmenu-btns border-top pt-3 ps-3 d-flex align-items-center justify-content-center'>
                            <Button type="default" className="bg-white text-dark border-0 col-md-5" onClick={() => { navigateLogin(); expandMenuOnClick(); }}>Log in</Button>
                            <Button type="primary" className='bg-lightblue col-md-5' onClick={() => { navigateLogin(); expandMenuOnClick(); }}>Get Started</Button>
                        </div>
                    </div> : ""}
                <Content className='pt-5'>
                    <div className=' pt-5 bg-layout-color' id="first-part">
                        <div className="d-flex px-0 col-md-8 mx-auto">
                            <div className='col'>
                                <a href="https://axoneegsolutions.com" target="_blank" rel="noopener noreferrer" className='inline-flex items-center gap-2 px-3 py-2 rounded-full bg-primary-landing'>
                                    <BrainIcon /> <span className="ps-1"></span>Powered by Axon EEG Solutions</a>
                                <div className='mt-3 header'>
                                    <h1 className="mb-0">Reduce Uncertainty.</h1>
                                    <h1 className="mb-0"><span className='text-gradient'>Think With Objective</span><span className='green-text'> Brain Data.</span> </h1>
                                </div>
                                <div className="mt-3">
                                    <p className='fs-20 normal-font'>Subjective assessments leave you uncertain. EEG Data Hub provides objective brain data to support clinical decision-making with greater clarity.</p>
                                </div>
                                <div className="my-4 text-start start-section">
                                    <Button type="primary" size="large" className='bg-lightblue text-white started col-md-5' onClick={navigateLogin} >Get Started Free <RightArrowIcon /></Button>
                                    <Button type="default" size="large" className='text-dark ms-3 col-md-5 text-decoration-none' href="#how-it-works" >See How It Works</Button>
                                </div>
                                <div className="guideline d-flex flex-wrap pt-3">
                                    <div className="col-md-6">
                                        <p className="normal-font fs-14 ">
                                            <HippaIcon /> HIPAA Complaint
                                        </p>
                                    </div>
                                    <div className="col-md-6">
                                        <p className="normal-font fs-14 my-auto">
                                            <EnergyIcon /> &lt;72 Hours or Less for Rush Orders
                                        </p>
                                    </div>
                                    <div className="col-md-6">
                                        <p className="normal-font fs-14 my-auto">
                                            <BrainIcon /> qEEG-DL and BCN (EEG) Certified
                                        </p>

                                    </div>
                                </div>

                            </div>
                            <div className='col-md-7 pe-0 ps-3 mx-auto'>
                                <div className="login-bg-container text-center h-100 col-md-10 mx-auto">
                                    <Image src={LoginImg} alt="login-img" preview={false} height="100%" width="auto" className="login-img" />
                                </div>
                            </div>
                        </div>
                        <div className="mt-5 text-center pt-5 mouse-icon">
                            <MouseIcon />
                        </div>
                    </div>
                    <div className='bg-white mt-5 pb-4' id="second-part">
                        <div className='col-md-12 mt-3 p-5'></div>
                        <div className='col-md-8 mx-auto'>
                            <p className='text-center center-prblm'>
                                <small className='text-uppercase'>The Problem</small>
                            </p>
                            <h1 className='text-center'>Move Beyond <span className='red-text text-center'>Guesswork</span></h1>
                            <div className="mt-3 col-md-8 mx-auto">
                                <p className='fs-18 normal-font text-center'>Mental health care has long relied on subjective assessments. But you deserve better tools to understand what&apos;s really happening in your patients&apos; brains.
                                </p>
                            </div>
                            <div className='mt-5 pb-5 col-md-10 row flexbox h-100 mx-auto justify-content-between'>
                                {problemItems?.map((item: any) => {
                                    return (
                                        <div className='col-md-4 d-flex' key={item.key}>
                                            <div className='border px-4 py-4 pblm-card'>
                                                <div className='mb-4 icon p-3'>
                                                    {item.icon}
                                                </div>
                                                <h5 className='fs-20 mb-3'>{item.title}</h5>
                                                <p className='normal-font fs-16'>{item.content}</p>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    </div>
                    <div className='bg-layout-color1  pb-5' id="third-part">
                        <div className='col-md-12 pt-5 pb-3'></div>
                        <div className='col-md-8 mx-auto d-flex'>
                            <div className='col-md-6 mt-3'>
                                <p className='text-start center-solution mt-5'>
                                    <small className='text-uppercase'>The Solution</small>
                                </p>
                                <h1 className='text-start'>Objective EEG Insights to <span className='gradient-text text-center'>Support Mental Health Care</span></h1>
                                <div className="mt-3 col-md-10">
                                    <p className='fs-18 normal-font text-start'>EEG Data Hub is your portal to objective brain data. Simply upload your EDF files and receive expert-reviewed qEEG reports that
                                        provide clinically meaningful context.
                                    </p>
                                </div>
                                <div className='mt-5 mx-0 solution-card col-md-10'>
                                    {solutionItems?.map((item: any) => {
                                        return (
                                            <div className='d-flex mb-2' key={item.key}>
                                                <div className="col-md-auto icon p-2 me-2">{item.icon}</div>
                                                <div className="col">
                                                    <h6 className='mb-2'>{item.title}</h6>
                                                    <p>{item.content}</p>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                                <div className="my-4 text-start start-section one">
                                    <Button type="primary" size="large" className='bg-lightblue text-white started col-md-5' onClick={navigateLogin}>Start Your First Report <RightArrowIcon /></Button>
                                </div>
                            </div>
                            <div className='col-md-6'>
                                <div className="carousel-wrapper">

                                    <Carousel effect="fade" autoplay >
                                        <div className='border-radius top'>
                                            <h6 className='d-inline-block p-2 top'>Sample from 30-Page Report</h6>
                                            <Image src={Image1} preview={false} height="100" />
                                            {/* <div className='text-end'> <h6 className='d-inline-block p-2 bottom text-end ms-auto'>Page 1 of 4</h6></div> */}

                                        </div>
                                        <div className='border-radius top'>
                                            <h6 className='d-inline-block p-2 top'>Sample from 30-Page Report</h6>
                                            <Image src={Image2} preview={false} height="100" />
                                            {/* <div className='text-end'><h6 className='d-inline-block p-2 bottom text-end ms-auto'>Page 2 of 4</h6></div>  */}
                                        </div>
                                        <div className='border-radius top'>
                                            <h6 className='d-inline-block p-2 top'>Sample from 30-Page Report</h6>
                                            <Image src={Image3} preview={false} height="100" />
                                            {/* <div className='text-end'> <h6 className='d-inline-block p-2 bottom text-end ms-auto'>Page 3 of 4</h6></div> */}
                                        </div>
                                        <div className='border-radius top'>
                                            <h6 className='d-inline-block p-2 top'>Sample from 30-Page Report</h6>
                                            <Image src={Image4} preview={false} height="100" />

                                            {/* <div className='text-end'> <h6 className='d-inline-block p-2 bottom text-end ms-auto'>Page 4 of 4</h6></div> */}
                                        </div>
                                    </Carousel>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='bg-white pt-5' id="how-it-works">
                        <div className='col-md-12 mt-3 p-5'></div>
                        <div className='col-md-8 mx-auto pb-5'>
                            <p className='text-center center-prblm'>
                                <small className='text-blue text-uppercase'>The Plan</small>
                            </p>
                            <h1 className='text-center'>Three Simple Steps to <span className='text-gradient'>Clarity</span></h1>
                            <div className="mt-3 col-md-6 mx-auto">
                                <p className='fs-18 normal-font text-center'>
                                    Getting started with qEEG analysis has never been easier. Our streamlined process gets you from raw data to actionable insights quickly.
                                </p>
                            </div>
                            <div className='mt-5 pb-5 col-md-10 row mx-auto plan-row justify-content-between'>
                                {plantItems?.map((item: any) => {
                                    return (
                                        <div className='col-md-4 pe-0' key={item.key}>
                                            <div className='border px-4 py-4 pblm-card bg-white plan-card col-md-11'>
                                                <div className='d-inline-block p-2 step-bg'>Step 0{item.key}</div>
                                                <div className='mb-4 icon p-3 mt-3'>
                                                    {item.icon}
                                                </div>
                                                <h5 className='fs-20 mb-3'>{item.title}</h5>
                                                <p className='normal-font fs-16 col-md-12 pe-3'>{item.content}</p>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                            <div className="mt-3 col-md-6 mx-auto">
                                <p className='fs-18 normal-font text-center'>
                                    Reports are intended to support clinical reasoning and decision-making by qualified professionals.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className='bg-white pt-3' id="pricing">
                        <div className='col-md-12  p-4'></div>
                        <div className='col-md-8 mx-auto pb-5'>
                            <p className='text-center center-prblm'>
                                <small className='text-green text-uppercase'>Pricing</small>
                            </p>
                            <h1 className='text-center'>Simple, <span className='text-gradient'>Transparent</span> Pricing</h1>
                            <div className="mt-3 col-md-6 mx-auto">
                                <p className='fs-18 normal-font text-center'>
                                    No subscriptions. No hidden fees. Pay per report as you need them.
                                </p>
                            </div>
                            <div>
                                <div className='col-md-5 pe-0 mx-auto mt-5'>
                                    <div className='border px-4 py-4 pblm-card bg-white plan-card price-card'>
                                        <div className='d-inline-block p-2  step-bg mx-auto text-center'>Per Report</div>
                                        <div className='mb-4 p-3 mt-3 text-center'>
                                            <div className='text-center fs-14 mb-2'><small className="fs-14">Starting at</small></div>
                                            <h1 className='text-center fs-60 mb-2'>$150<sub className='normal-font fs-16'>USD</sub></h1>
                                            <div className='text-center mb-4'><small className="fs-14">Final pricing based on report selections</small></div>
                                            <div className='text-center'><span className='fs-18'>&lt;72 hours</span> <small>or less for rush orders</small></div>
                                        </div>
                                        <div className='col-md-11 px-3'>
                                            {priceDetails?.map((item: any) => {
                                                return (
                                                    <div key={item.key} className=' d-flex my-auto'>
                                                        <div className="icon-circle col-md-auto"><CheckOutlined /></div><p className="ps-2 fs-14 normal-font">{item.content}</p>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        <div className="my-4 text-start start-section one px-3">
                                            <Button type="primary" size="large" className='bg-lightblue text-white started col-md-12' onClick={navigateLogin} >Get Started <RightArrowIcon /></Button>
                                        </div>
                                    </div>
                                </div>
                                <div className='mt-5 p-4 border d-flex col-md-7 mx-auto staff-complex'>
                                    <div className="col-md-auto icon">
                                        <UsersIcon />
                                    </div>
                                    <div className="col ps-3">
                                        <h5>Expert Staff for Complex Cases</h5>
                                        <p className='normal-font fs-16 mb-3'>All reports are reviewed by <span className="fw-bold text-dark">Dr. Steve Rondeau, BCN(EEG), qEEG-DL.</span> Our team includes on-staff
                                            neurology consultation available for complex cases, ensuring access to expert interpretation when clinically appropriate.</p>
                                        <div className='d-flex align-items-center '>
                                            <div className='light-blue-bg px-2 py-1 fs-12'><small>BCN(EEG) Certified</small></div>
                                            <div className='light-green-bg px-2 py-1 mx-3 fs-12'><small>qEEG-DL Certified</small></div>
                                            <div className='no-bg px-2 py-1 fs-12'><small>On-Staff Neurology</small></div>
                                        </div>
                                    </div>
                                </div>
                                <div className='mt-4 text-center pt-2 mb-5'>
                                    <small className='fs-14 normal-font'>Volume discounts available for high-volume practices. Contact <a className='text-blue normal-font'>info@axoneegsolutions.com</a></small>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='bg-layout-color1 pt-5 pb-5' id="sixth-part">
                        <div className='col-md-12 pt-5 pb-4 '></div>
                        <div className='col-md-7 mx-auto d-flex'>
                            <div className='col-md-6 text-start'>
                                <p className='text-start center-prblm mb-3'>
                                    <small className='text-green text-uppercase'>Not ready for full reports?</small>
                                </p>
                                <h1 className='fs-36'>Start with <span className='text-gradient'>AxonWave</span></h1>
                                <div className="mt-3 solution-card">
                                    <p className='fs-18 normal-font text-start mb-4'>
                                        Explore EEG visualizations for personal insight and education. All you need is an EDF file. AxonWave gives you instant access to non-diagnostic qEEG visualization right on your iPhone.
                                    </p>
                                    {waveItems?.map((item: any) => {
                                        return (
                                            <div key={item.key} className='d-flex mb-2'>
                                                <div className='icon col-md-auto'>{item.icon}</div>
                                                <div className='col ps-3'>
                                                    <h6>{item.title}</h6>
                                                    <p className='normal-font fs-14'>{item.content}</p>
                                                </div>
                                            </div>
                                        )
                                    })}
                                    <div className='text-start col-md-6 mt-3'>
                                        <Button type="primary" className='bg-green text-white px-4' onClick={navigateAppStore}><MobileOutlined className="pe-1" />Download on App Store <RightArrowIcon /></Button>
                                    </div>

                                </div>

                            </div>
                            <div className='col-md-6 d-flex align-items-center justify-content-end'>
                                <div className='col-md-9 bg-white axon-wave-bg mx-auto border px-4 py-3'>
                                    <Image preview={false} src={AxonWaveImg} height={130} className='my-2' />
                                    <div className='d-flex mt-4 mb-3 mt-3 col-md-7 mx-auto'>
                                        <div className='light-green-bg fs-12 px-2 py-1 normal-font'>
                                            Free Download
                                        </div>
                                        <div className='light-green-bg ms-3 fs-12 px-2 py-1 normal-font'>
                                            iOS App Store
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                    <div className='bg-layout-color1 pt-5 pb-5' id="faq">
                        <div className='col-md-12 pt-5 pb-3'></div>
                        <div className='col-md-8 pt-3 mx-auto pb-5'>
                            <p className='text-center center-prblm'>
                                <small className='text-green text-uppercase'>FAQ</small>
                            </p>
                            <h1 className='text-center fs-48'>Questions <span className='text-gradient'>We&apos;ve Got Answers.</span></h1>
                            <div className="mt-3 col-md-6 mx-auto">
                                <p className='fs-18 normal-font text-center'>
                                    Everything you need to know about getting started with EEG Data Hub.
                                </p>
                            </div>
                            <div className='col-md-6 mx-auto mt-4'>
                                <Collapse accordion items={faqItems} />
                            </div>
                        </div>
                    </div>
                    <div id="eight-part" style={{
                        background: 'linear-gradient(135deg, hsl(207, 75%, 28%) 0%, hsl(207, 70%, 38%) 50%, hsl(180, 50%, 42%) 100%)',
                        // background: 'linear-gradient(90deg, #1a5a8e 0%,#1a5a8e 25%, #4a90c8 80%,#36a1a1 100%)',
                        // minHeight: '400px',
                        padding: '60px 20px',
                        textAlign: 'center',
                        color: 'white'
                    }}>
                        <h1 className="col-md-5 mx-auto">Ready to Bring Clarity to Your Practice?</h1>
                        <p className="normal-font col-md-5 mx-auto fs-20 mt-3 text-center">Join forward-thinking providers who are using objective brain data to transform mental health care.
                            Your first report could be ready tomorrow.</p>
                        <div className='text-center mt-5 col-md-5 practice-bottom pb-5 mx-auto'>
                            <Button type="primary" className='bg-white text-blue px-4' onClick={navigateLogin}><span className='fs-18 ps-2'>Create Free Account</span><span className='pe-2'><RightArrowIcon /></span></Button>
                        </div>
                        <div className='pt-4 text-center'>
                            <p className='normal-font fade-white fs-14'>
                                Questions? Reach out directly:
                            </p>
                            <div className='d-flex col-md-7 mx-auto text-white align-items-center justify-content-center mb-3'>
                                <div className="d-flex align-items-center justify-content-center">
                                    <div className='icon'><MailOutlined /></div>
                                    <p className='normal-font fs-16 text-white ps-1 my-auto'>
                                        info@axoneegsolutions.com
                                    </p>
                                </div>
                                <div className="d-flex ms-2 align-items-center justify-content-center">
                                    <div className='icon pe-1'><PhoneOutlined /></div>
                                    <p className='normal-font fs-16 text-white my-auto'>
                                        (720) 860-3111
                                    </p>
                                </div>

                            </div>
                            <p className='normal-font text-center fs-14 '>
                                <MailOutlined /> Dr. Steven Rondeau, BCN(EEG), QEEG-DL
                            </p>
                        </div>
                    </div>
                    <div className='bg-layout-color1 pt-5 pb-4' id="ninth-part">
                        <div className='col-md-12 mt-3 p-3'></div>
                        <div className='col-md-8 mx-auto pb-4'>
                            <div className='text-center'>
                                <img src={AxonImg} height="64px" width="auto" />
                                {/* <Image preview={false} src={AxonImg} height={64} /> */}
                            </div>
                            <h1 className='text-center fs-36 mt-4'>Part of the Axon Ecosystem </h1>
                            <div className="mt-3 col-md-7 mx-auto">
                                <p className='fs-18 normal-font text-justify'>
                                    EEG Data Hub is powered by <span className='fw-bold'>Axon EEG Solutions</span> — the leader in qEEG analysis, expert interpretation,
                                    and neuroscience innovation. Explore our full suite of tools and services.
                                </p>
                            </div>
                            <div className='row mx-0 flexbox flexbox1 h-100 col-md-10 mx-auto mt-5'>
                                {ecoItems?.map((item: any) => {
                                    return (
                                        <div key={item.key} className="col-md-3 h-100">
                                            <div className={`${item.key == 1 ? "first-one" : "border"} bg-white d-flex flex-column align-items-center justify-content-center  px-3 pt-4 pb-2 h-100`} onClick={() => ecoClick(item)}>
                                                {item.key == 1 ? <div className='step-bg px-2 py-1 mb-3'>You are here</div> : ""}
                                                <div className={`${item.key == 4 || item.key == 3 ? "icon mb-2" : item.key == 2 ? "mb-4 mt-2" : "mb-2 mt-1"}`}>{item.icon}</div>
                                                <h6 className={`${item.key == 2 ? "mt-2 pt-1 mb-2" : "mt-3 mb-2"} text-center`}>{item.title}</h6>
                                                <p className='normal-font fs-14 text-center'>{item.content}</p>
                                                <div className='icon-link'>
                                                    {item.key == 1 ? <span className="hidden"><ExternalLinkIcon /> </span> : <ExternalLinkIcon />}
                                                </div>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                            <div className="mt-4 pt-3 text-center text-blue normal-font fs-16 icon learn-sec pointer" onClick={navigateAxon}>
                                <UsersIcon /> Learn more about Axon EEG Solutions <ExternalLinkIcon />
                            </div>
                        </div>
                    </div>
                    <div className='bg-white pt-5 pb-' id="ninth-part">
                        {/* <div className='col-md-12 mt-3 p-3'></div> */}
                        <div className='col-md-8 mx-auto pb-4'>
                            <div className='text-center'>
                                <img src={AxonImg} height="56px" width="auto" />
                                {/* <Image preview={false} src={AxonImg} height={56} /> */}
                            </div>
                            <div className="mt-3 col-md-6 mx-auto">
                                <p className='fs-14 normal-font text-center'>
                                    EEG Data Hub is the data processing platform of <span className='fw-bold text-dark'>Axon EEG Solutions</span> —
                                    leaders in qEEG analysis, expert interpretation, and neuroscience research. EEG Data Hub provides EEG data processing,
                                    quantitative analysis, and expert interpretation to support qualified healthcare and allied health professionals. Services and reports are intended
                                    for clinical decision support only and do not diagnose, treat, cure, or prevent disease, nor do they provide automated medical decision-making.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className='landing-footer'>
                        <div className='py-5'>
                            <div className='col-md-9 mx-auto practice-bottom'>
                                <div className='d-flex align-items-center justify-content-center'>
                                    <p className="normal-font fade-white icon mb-0" onClick={navigateAppStore}>AxonWave App <ExternalLinkIcon /></p>
                                    <p className="normal-font fade-white icon mx-4 mb-0" onClick={navigateAxon}>Research Partnerships <ExternalLinkIcon /></p>
                                    <p className="normal-font fade-white icon mb-0" onClick={navigateAxon}>Publications <ExternalLinkIcon /></p>
                                </div>
                                <div className='row mx-0 col-md-3 mx-auto mt-4 pb-4 pt-2 mb-2'>
                                    <div className="col-md-6 d-flex align-items-center justify-content-center ms-auto">
                                        <div className='icon text-white'><MailOutlined /></div>
                                        <p className='normal-font fs-14 text-white ps-1 my-auto'>
                                            info@axoneegsolutions.com
                                        </p>
                                    </div>
                                    <div className="col-md-6 d-flex align-items-center justify-content-center me-auto">
                                        <div className='icon pe-1 text-white'><PhoneOutlined /></div>
                                        <p className='normal-font fs-14 text-white my-auto'>
                                            (720) 860-3111
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className='col-md-9 mx-auto align-items-center justify-content-center text-center mt-4 pt-2'>
                                <small className='italic fade-white fs-12'>Improving Mental Health Through Precision and Clarity™</small>
                                <div>
                                    <p className='normal-font fade-white mt-3'>© {currentYear} Healthy Paths, Inc.</p>
                                </div>
                            </div>
                        </div>

                    </div>
                </Content>
            </Layout>
        </div>
    )
}

export default LandingPage;